<template>
  <Popover v-if="accountEnabled">
    <template #button="{ open }">
      <AccountIconHeader :is-active="open || loggedIn" />
    </template>

    <template #header>
      <div class="bg-br-green-light fluid-xs leading-tightest py-px">
        <div class="flex items-center min-h-[48px]">
          <div v-if="loggedIn" class="whitespace-nowrap flex items-center justify-between min-w-[180px] w-full">
            <span class="mr-6">{{ $t('account.welcome', { name: currentUserName }) }}</span>
            <MenuItem v-slot="{ active }">
              <button
                class="hover:no-underline focus:no-underline text-blackly-dark hover:text-black focus:text-black underline transition-colors duration-200 px-2 py-1 border-2 border-white ui-active:border-br-green-dark"
                :class="[active ? 'text-br-green no-underline' : '']"
                @click="handleLogout"
              >
                {{ $t('account.logout') }}
              </button>
            </MenuItem>
          </div>
          <div v-else>
            <ul
              v-if="benefits && benefits.length > 0"
              class="whitespace-nowrap fluid-sm py-3 space-y-2 font-light text-xs"
            >
              <li v-for="item in benefits" :key="item.id">
                <CheckmarkIcon class="text-br-green h-2 mr-1" /> {{ item.description }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>

    <div v-if="loggedIn">
      <MenuItem
        :as="LinkField"
        class="fluid-sm tracking-wider whitespace-nowrap px-5 py-3 font-normal text-black no-underline transition-all hover:bg-putty flex items-center gap-2 border-2 border-white ui-active:border-br-green-dark"
        :to="$localeRootPath('/account/profile/details')"
      >
        <AccountIcon class="h-5 w-5 text-br-green fill-current mr-2" />
        {{ $t('account.profileTitle') }}
      </MenuItem>
      <MenuItem
        :as="LinkField"
        class="fluid-sm tracking-wider whitespace-nowrap px-5 py-3 font-normal text-black no-underline transition-all hover:bg-putty flex items-center gap-2 border-2 border-white ui-active:border-br-green-dark"
        :to="$localeRootPath('/account/orders')"
      >
        <ProductIcon class="h-5 w-5 text-br-green fill-current mr-2" />
        {{ $t('account.ordersTitle') }}
      </MenuItem>
      <MenuItem
        :as="LinkField"
        class="fluid-sm tracking-wider whitespace-nowrap px-5 py-3 font-normal text-black no-underline transition-all hover:bg-putty flex items-center gap-2 border-2 border-white ui-active:border-br-green-dark"
        :to="$localeRootPath('/account/exercises')"
      >
        <ExerciseIcon class="h-5 w-5 text-br-green fill-current mr-2" />
        {{ $t('account.exercisesTitle') }}
      </MenuItem>
      <MenuItem
        v-if="locale === 'de'"
        :as="LinkField"
        class="fluid-sm tracking-wider whitespace-nowrap px-5 py-3 font-normal text-black no-underline transition-all hover:bg-putty flex items-center gap-2 border-2 border-white ui-active:border-br-green-dark"
        :to="$localeRootPath('/account/courses')"
      >
        <AcademyIcon class="h-5 w-5 text-br-green fill-current mr-2" />
        {{ $t('account.coursesTitle') }}
      </MenuItem>
    </div>
    <template v-else>
      <MenuItem
        :as="LinkField"
        :to="$localeRootPath('/account/login')"
        data-testid="btn-account-login"
        class="fluid-sm tracking-wider whitespace-nowrap block px-5 py-3 font-normal text-black no-underline transition-all hover:bg-putty border-2 border-white ui-active:border-br-green-dark"
        @click.native="handleLoginClick"
      >
        {{ $t('navigation.login') }}</MenuItem
      >
      <MenuItem
        :as="LinkField"
        :to="$localeRootPath('/account/register')"
        data-testid="btn-account-register"
        class="fluid-sm tracking-wider whitespace-nowrap block px-5 py-3 font-normal text-black transition-all hover:bg-putty border-2 border-white ui-active:border-br-green-dark"
        @click.native="handleRegisterClick"
      >
        {{ $t('navigation.register') }}</MenuItem
      >
    </template>
  </Popover>
</template>

<script setup>
import AccountIconHeader from '@/components/layout/header/AccountIcon.vue';
import AccountIcon from '@/components/icons/AccountIcon.vue';
import Popover from '@/components/layout/header/Popover.vue';
import CheckmarkIcon from '@/components/icons/CheckmarkIcon';
import LinkField from '@/components/common/LinkField.vue';
import { MenuItem } from '@headlessui/vue';
import { useMainNavStore } from '@/stores/main-nav';
import { useBlackrollAuth } from '@/composables/useBlackrollAuth';
import ProductIcon from '@/components/icons/ProductIcon';
import ExerciseIcon from '@/components/icons/ExerciseIcon';
import AcademyIcon from '@/components/icons/AcademyIcon';
import { useI18n } from 'vue-i18n';

const { $config, $_gtm, $localeRootPath } = useNuxtApp();
const mainNavStore = useMainNavStore();
const { locale } = useI18n();

const benefits = computed(() => mainNavStore.benefits);
const accountEnabled = $config.public.auth.isEnabled;

const { loggedIn, user, logout } = useBlackrollAuth();

const currentUserName = computed(() => {
  if (!user.value) return '';
  return user.value.displayName || user.value.email || '';
});

const handleLoginClick = function () {
  const clickData = {
    event: 'gaEvent',
    event_name: 'login_click',
    login_click: {
      event_category: 'Account',
      event_action: 'Login',
      event_label: 'Click',
    },
  };
  $_gtm.push(clickData);
};

const handleRegisterClick = function () {
  const clickData = {
    event: 'gaEvent',
    event_name: 'sign_up_click',
    sign_up_click: {
      event_category: 'Account',
      event_action: 'Sign Up',
      event_label: 'Click',
    },
  };
  $_gtm.push(clickData);
};

const handleLogout = async () => {
  await logout();

  $_gtm.push({
    event: 'gaEvent',
    event_name: 'logout',
    logout: {
      event_category: 'Account',
      event_action: 'Logout',
      event_label: 'Success',
    },
  });

  navigateTo($localeRootPath('/account/login'));
};
</script>
